import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import { Container } from '../components/layout/Primitives';
import JournalTableOfContents from '../components/journal/JournalTableOfContents';
import JournalOffersAside from '../components/journal/JournalOffersAside';
import PageSeo from '../components/seo/PageSeo';
import ApiLocaleSeo from '../components/seo/ApiLocaleSeo';
import { Button } from '../components/ui/Button';
import { isApiEnabled } from '../config/env';
import { useLanguage } from '../contexts/LanguageContext';
import {
  getPostBySlug,
  getPrevNext,
  getRelatedPosts,
  normalizePost,
  normalizePrevNext,
  normalizeRelated,
} from '../services/blogService';
import { hasBlogContentHtml, normalizeBlogContentHtml } from '../utils/blogContent';
import { prepareJournalArticleHtml } from '../utils/journalArticleToc';
import { stripSeoRelFromInternalAnchors } from '../utils/richtextInternalDofollow';

const p = 'modamai.blogPost';
const listP = 'modamai.blog';

const PageWrap = styled.div`
  padding: clamp(24px, 3vw, 40px) 0 clamp(40px, 5vw, 64px);
  width: 100%;
  max-width: 100%;
  overflow-x: clip;
`;

const postShellWidth = css`
  width: min(100%, 94%);
  max-width: 72rem;
  margin-left: auto;
  margin-right: auto;
`;

const Breadcrumb = styled.nav`
  margin-bottom: 20px;
  font-size: ${({ theme }) => theme.type.bodyXs};
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.mutedForeground};

  a {
    color: inherit;
    text-decoration: none;
    &:hover {
      color: ${({ theme }) => theme.colors.foreground};
    }
  }

  span[aria-hidden] {
    margin: 0 0.5rem;
  }
`;

const EditorialShell = styled.div`
  ${postShellWidth}
  max-width: 100%;
  overflow-x: clip;
`;

const BackRow = styled.div`
  margin-bottom: 20px;
  ${postShellWidth}
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: ${({ theme }) => theme.type.bodyXs};
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.mutedForeground};
  text-decoration: none;

  &:hover {
    color: ${({ theme }) => theme.colors.foreground};
  }
`;

const Hero = styled.div`
  margin: 0 0 32px;
  width: 100%;
`;

const HeroFrame = styled.div`
  aspect-ratio: 16 / 9;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.secondary};

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    aspect-ratio: 2 / 1;
  }
`;

const HeroImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const OffersRail = styled.div`
  min-width: 0;
  width: 100%;

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    padding-left: clamp(20px, 2vw, 32px);
    margin-left: clamp(4px, 0.5vw, 8px);
    border-left: 1px solid ${({ theme }) => theme.colors.border};
  }
`;

const BodyGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: clamp(28px, 4vw, 40px);
  align-items: start;
  width: 100%;

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: ${({ $withSidebar }) =>
      $withSidebar ? 'minmax(0, 1fr) minmax(280px, 320px)' : 'minmax(0, 1fr)'};
    column-gap: clamp(32px, 3vw, 48px);
  }
`;

const ArticleMain = styled.article`
  min-width: 0;
  width: 100%;
`;

const ArticleHeader = styled.header`
  width: 100%;
  text-align: center;
  margin-bottom: 28px;
  padding-bottom: 24px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const Meta = styled.p`
  margin: 0 0 14px;
  font-size: ${({ theme }) => theme.type.bodyXs};
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  margin-bottom: 16px;
`;

const TagPill = styled(Link)`
  padding: 4px 10px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  font-size: 0.65rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.mutedForeground};
  text-decoration: none;
  transition: border-color 0.2s ease, color 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.foreground};
    color: ${({ theme }) => theme.colors.foreground};
  }
`;

const Title = styled.h1`
  margin: 0 0 16px;
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(1.85rem, 2.5vw + 1rem, 2.65rem);
  font-weight: 300;
  line-height: 1.12;
  letter-spacing: 0.02em;
`;

const Dek = styled.p`
  margin: 0;
  font-size: clamp(1rem, 0.5vw + 0.9rem, 1.125rem);
  line-height: 1.65;
  color: ${({ theme }) => theme.colors.mutedForeground};
  max-width: 36rem;
  margin-left: auto;
  margin-right: auto;
`;

const ArticleBody = styled.div`
  font-size: ${({ theme }) => theme.type.body};
  line-height: 1.8;
  color: ${({ theme }) => theme.colors.foreground};
  max-width: 100%;
  overflow-wrap: anywhere;
  word-break: break-word;

  * {
    max-width: 100%;
  }

  table {
    display: block;
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  pre,
  iframe,
  video,
  embed {
    max-width: 100%;
  }

  p {
    margin: 0 0 1.25em;
  }

  h2,
  h3 {
    font-family: ${({ theme }) => theme.fonts.display};
    font-weight: 400;
    margin: 2em 0 0.75em;
    line-height: 1.25;
    letter-spacing: 0.02em;
    scroll-margin-top: calc(var(--header-height, 72px) + 16px);
  }

  h2 {
    font-size: clamp(1.35rem, 1vw + 1.1rem, 1.75rem);
  }

  h3 {
    font-size: clamp(1.1rem, 0.8vw + 1rem, 1.35rem);
  }

  ul,
  ol {
    margin: 0 0 1.25em;
    padding-left: 1.35em;
  }

  li {
    margin-bottom: 0.35em;
  }

  a {
    color: inherit;
    text-decoration: underline;
    text-underline-offset: 3px;
    text-decoration-thickness: 1px;

    &:hover {
      opacity: 0.75;
    }
  }

  img {
    max-width: 100%;
    height: auto;
    border-radius: 2px;
    margin: 1.75em 0;
  }

  blockquote {
    margin: 1.75em 0;
    padding: 0.25em 0 0.25em 1.25rem;
    border-left: 2px solid ${({ theme }) => theme.colors.foreground};
    font-family: ${({ theme }) => theme.fonts.display};
    font-size: 1.15em;
    line-height: 1.5;
    color: ${({ theme }) => theme.colors.mutedForeground};
  }

  details[data-faq] {
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: 4px;
    padding: 0.75rem 1rem;
    margin: 1.25em 0;
    background: ${({ theme }) => theme.colors.secondary};
  }

  details[data-faq] summary {
    cursor: pointer;
    font-weight: 600;
    font-size: ${({ theme }) => theme.type.bodySm};
    list-style: none;
  }

  details[data-faq] summary::-webkit-details-marker {
    display: none;
  }

  details[data-faq] > *:not(summary) {
    margin-top: 0.75rem;
    font-size: ${({ theme }) => theme.type.bodySm};
    color: ${({ theme }) => theme.colors.mutedForeground};
  }
`;

const NavRow = styled.nav`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  justify-content: space-between;
  margin-top: 48px;
  padding-top: 28px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const NavLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: ${({ theme }) => theme.type.bodySm};
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.foreground};
  text-decoration: none;
  max-width: min(100%, 260px);
  line-height: 1.35;

  &:hover {
    text-decoration: underline;
  }
`;

const RelatedSection = styled.section`
  margin-top: 56px;
  padding-top: 40px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  ${postShellWidth}
`;

const RelatedTitle = styled.h2`
  margin: 0 0 24px;
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(1.25rem, 1vw + 1rem, 1.5rem);
  font-weight: 300;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  text-align: center;
`;

const RelatedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px 16px;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 24px;
  }
`;

const RelatedCard = styled(Link)`
  text-decoration: none;
  color: inherit;
  display: flex;
  flex-direction: column;
`;

const RelatedThumb = styled.div`
  aspect-ratio: 4 / 3;
  overflow: hidden;
  margin-bottom: 10px;
  background: ${({ theme }) => theme.colors.secondary};
`;

const RelatedImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s ease;

  ${RelatedCard}:hover & {
    transform: scale(1.04);
  }
`;

const RelatedH = styled.h3`
  margin: 0 0 4px;
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: ${({ theme }) => theme.type.bodySm};
  font-weight: 400;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const RelatedExcerpt = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.type.bodyXs};
  color: ${({ theme }) => theme.colors.mutedForeground};
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Empty = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const ApiOff = styled.p`
  text-align: center;
  padding: 48px 24px;
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

function formatDate(iso, localeCode) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(localeCode || 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return '';
  }
}

function estimateReadMinutes(html) {
  const text = String(html || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!text) return 0;
  return Math.max(1, Math.ceil(text.split(/\s+/).length / 200));
}

export default function BlogPost() {
  const { t } = useTranslation();
  const { slug } = useParams();
  const { locale } = useLanguage();
  const useApi = isApiEnabled();

  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [prevNext, setPrevNext] = useState({ prev: null, next: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = () => {
    if (!useApi || !slug) return;
    setLoading(true);
    setError('');
    Promise.all([
      getPostBySlug(slug, locale),
      getRelatedPosts(slug, locale, 4),
      getPrevNext(slug, locale),
    ])
      .then(([postRes, relatedRes, pnRes]) => {
        setPost(normalizePost(postRes));
        setRelatedPosts(normalizeRelated(relatedRes));
        setPrevNext(normalizePrevNext(pnRes));
      })
      .catch((e) => {
        setError(e?.response?.data?.message || e.message || t(`${p}.loadPostFailed`));
        setPost(null);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!useApi || !slug) return undefined;
    window.scrollTo(0, 0);
    load();
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when slug/locale changes
  }, [slug, locale, useApi]);

  const contentHtml = useMemo(() => normalizeBlogContentHtml(post?.content), [post?.content]);

  const safeArticleHtml = useMemo(() => {
    if (!hasBlogContentHtml(contentHtml)) return '';
    return stripSeoRelFromInternalAnchors(contentHtml);
  }, [contentHtml]);

  const { html: articleHtml, entries: tocEntries } = useMemo(
    () => prepareJournalArticleHtml(safeArticleHtml),
    [safeArticleHtml]
  );

  const postOffers = useMemo(() => {
    const raw = post?.offers;
    if (!Array.isArray(raw)) return [];
    return raw.filter((o) => o?.title?.trim() && o?.url?.trim());
  }, [post?.offers]);

  const tagJournalHref = (tag) => `/blog?tag=${encodeURIComponent(tag)}`;

  if (!useApi) {
    return (
      <Container>
        <ApiOff>
          {t(`${p}.postApiOffBefore`)} <code>REACT_APP_API_URL</code> {t(`${p}.postApiOffAfter`)}
        </ApiOff>
      </Container>
    );
  }

  if (!slug) return null;

  if (loading && !post) {
    return (
      <Container>
        <Empty>{t(`${p}.loadingArticle`)}</Empty>
      </Container>
    );
  }

  if (error && !post) {
    return (
      <Container>
        <Breadcrumb aria-label={t('modamai.shop.breadcrumbAria')}>
          <Link to="/">{t('modamai.shop.home')}</Link>
          <span aria-hidden>/</span>
          <Link to="/blog">{t(`${listP}.title`)}</Link>
        </Breadcrumb>
        <Empty>
          <p>{error}</p>
          <Button type="button" $variant="outlineLuxe" onClick={load} style={{ marginTop: 16 }}>
            {t(`${listP}.retry`)}
          </Button>
        </Empty>
      </Container>
    );
  }

  if (!post) return null;

  const dek = post.excerpt || post.metaDescription || '';
  const postTags = Array.isArray(post.tags) ? post.tags.filter(Boolean) : [];
  const readMinutes = estimateReadMinutes(safeArticleHtml || dek);
  const hasOffersSidebar = postOffers.length > 0;

  return (
    <Container>
      <PageSeo documentTitle={post.title} description={dek || post.metaDescription || ''} />
      <ApiLocaleSeo
        canonicalUrl={post.canonicalUrl}
        hreflangAlternates={post.hreflangAlternates}
        hreflangLanguages={post.hreflangLanguages}
      />
      <PageWrap>
        <Breadcrumb aria-label={t('modamai.shop.breadcrumbAria')}>
          <Link to="/">{t('modamai.shop.home')}</Link>
          <span aria-hidden>/</span>
          <Link to="/blog">{t(`${listP}.title`)}</Link>
          <span aria-hidden>/</span>
          <span>{post.title}</span>
        </Breadcrumb>

        <BackRow>
          <BackLink to="/blog">
            <ArrowLeft size={16} aria-hidden />
            {t(`${p}.backToJournal`)}
          </BackLink>
        </BackRow>

        <EditorialShell>
          {post.featuredImage ? (
            <Hero>
              <HeroFrame>
                <HeroImg src={post.featuredImage} alt="" />
              </HeroFrame>
            </Hero>
          ) : null}

          <ArticleHeader>
            <Meta>
              {formatDate(post.publishedAt, locale)}
              {readMinutes > 0 ? ` · ${t(`${p}.readTime`, { minutes: readMinutes })}` : ''}
            </Meta>
            {postTags.length > 0 ? (
              <TagRow>
                {postTags.map((tag) => (
                  <TagPill key={tag} to={tagJournalHref(tag)}>
                    {tag}
                  </TagPill>
                ))}
              </TagRow>
            ) : null}
            <Title>{post.title}</Title>
            {dek ? <Dek>{dek}</Dek> : null}
          </ArticleHeader>

          <BodyGrid $withSidebar={hasOffersSidebar}>
            <ArticleMain>
              <JournalTableOfContents entries={tocEntries} />

              {hasBlogContentHtml(safeArticleHtml) ? (
                <ArticleBody dangerouslySetInnerHTML={{ __html: articleHtml }} />
              ) : dek ? (
                <ArticleBody as="div">
                  <p>{dek}</p>
                </ArticleBody>
              ) : (
                <ArticleBody as="div">
                  <p>{t(`${p}.noContent`)}</p>
                </ArticleBody>
              )}

              <NavRow aria-label={t(`${p}.postNavAria`)}>
                {prevNext.prev?.slug ? (
                  <NavLink to={`/blog/${prevNext.prev.slug}`}>
                    <ChevronLeft size={18} aria-hidden />
                    <span>{prevNext.prev.title || t(`${p}.postPrevious`)}</span>
                  </NavLink>
                ) : (
                  <span />
                )}
                {prevNext.next?.slug ? (
                  <NavLink
                    to={`/blog/${prevNext.next.slug}`}
                    style={{ textAlign: 'right', flexDirection: 'row-reverse', marginLeft: 'auto' }}
                  >
                    <span>{prevNext.next.title || t(`${p}.postNext`)}</span>
                    <ChevronRight size={18} aria-hidden />
                  </NavLink>
                ) : (
                  <span />
                )}
              </NavRow>
            </ArticleMain>
            {hasOffersSidebar ? (
              <OffersRail>
                <JournalOffersAside offers={postOffers} />
              </OffersRail>
            ) : null}
          </BodyGrid>
        </EditorialShell>

        {relatedPosts.length > 0 ? (
          <RelatedSection>
            <RelatedTitle>{t(`${p}.postRelated`)}</RelatedTitle>
            <RelatedGrid>
              {relatedPosts.map((rel) => {
                const s = rel.slug || rel._id;
                if (!s) return null;
                return (
                  <RelatedCard key={rel._id || s} to={`/blog/${s}`}>
                    <RelatedThumb>
                      {rel.featuredImage ? (
                        <RelatedImg src={rel.featuredImage} alt="" loading="lazy" />
                      ) : null}
                    </RelatedThumb>
                    <RelatedH>{rel.title}</RelatedH>
                    {rel.excerpt ? <RelatedExcerpt>{rel.excerpt}</RelatedExcerpt> : null}
                  </RelatedCard>
                );
              })}
            </RelatedGrid>
          </RelatedSection>
        ) : null}
      </PageWrap>
    </Container>
  );
}
